import React, { useState, useEffect } from 'react';
import classNames from 'classnames';

import IconButton from 'ui/IconButton';
import IconChevronLeft from 'ui/icons/ChevronLeft';
import IconChevronRight from 'ui/icons/ChevronRight';
import styles from './index.module.scss';

import TabItem, { TabDataType } from './TabItem';

export type { TabDataType };

export type TabsProps = {
  className?: string;
  tabItemClassName?: string;
  elementId: string;
  isLoading?: boolean;
  items: Array<TabDataType>;
  selectedItemId?: TabDataType['id'];
  withTabChangeButtons?: boolean;
  onTabChange?: (id: TabDataType['id']) => void;
};

const ADDITIONAL_DISTANCE = 30; // px

const scrollTabIntoView = (id: string | number, elementId: string) => {
  const parent = document.getElementById(`${elementId}`);
  const target = document.getElementById(`${elementId}_${id}`);

  if (!parent || !target) return;

  const leftDifference = parent.getBoundingClientRect().left - target.getBoundingClientRect().left;
  if (leftDifference > 0) {
    parent.scrollLeft -= leftDifference + ADDITIONAL_DISTANCE;
    return;
  }

  const rightDifference = target.getBoundingClientRect().right - parent.getBoundingClientRect().right;
  if (rightDifference > 0) {
    parent.scrollLeft += rightDifference + ADDITIONAL_DISTANCE * 2;
  }
};

const findIndex = (items: Array<TabDataType>, selectedItemId?: TabDataType['id']) =>
  items.findIndex((it) => it.id === selectedItemId);

const Tabs = ({
  className,
  tabItemClassName,
  elementId,
  items,
  isLoading = false,
  selectedItemId: selectedItemIdProp,
  withTabChangeButtons = false,
  onTabChange,
}: TabsProps) => {
  const initialItemId = selectedItemIdProp ?? items[0].id;
  const [selectedItemId, setSelectedItemId] = useState<string | number>(initialItemId);
  const [currentIndex, setCurrentIndex] = useState(0);

  const onTabChangeWrapper = (id: TabDataType['id']) => {
    scrollTabIntoView(id, elementId);

    setSelectedItemId(id);
    onTabChange?.(id);
  };

  useEffect(() => {
    setSelectedItemId(initialItemId);
  }, [initialItemId]);

  const onLeftClick = () => {
    onTabChangeWrapper(items[currentIndex - 1].id);
  };

  const onRightClick = () => {
    onTabChangeWrapper(items[currentIndex + 1].id);
  };

  useEffect(() => {
    const index = findIndex(items, selectedItemId);
    setCurrentIndex(index);

    if (index === 0 && withTabChangeButtons) {
      const parent = document.getElementById(`${elementId}`);
      parent?.scroll(0, 0);
    }
  }, [items, selectedItemId, elementId]);

  useEffect(() => {
    const index = items?.length ? findIndex(items, selectedItemId) : -1;
    if (index >= 0) {
      onTabChangeWrapper(items[index]?.id);
    }
  }, []);

  const showLeftButton = withTabChangeButtons ? currentIndex > 0 : false;
  const showRightButton = withTabChangeButtons ? currentIndex !== items.length - 1 : false;

  return (
    <section
      id={elementId}
      data-testid={elementId}
      role="tabpanel"
      className={classNames(styles.Tabs, className)}>
      {showLeftButton && (
        <IconButton
          className={styles.left}
          onClick={onLeftClick}
          elementId={`tabs_${elementId}_left_button`}>
          <IconChevronLeft />
        </IconButton>
      )}
      {items.map((item) => (
        <TabItem
          key={`tab_${item.id}`}
          id={item.id}
          className={tabItemClassName}
          isLoading={isLoading}
          isSelected={selectedItemId === item.id}
          isDisabled={item.isDisabled}
          elementId={`${elementId}_${item.id}`}
          onTabChange={onTabChangeWrapper}>
          {item.content}
        </TabItem>
      ))}
      {showRightButton && (
        <IconButton
          onClick={onRightClick}
          className={styles.right}
          elementId={`tabs_${elementId}_right_button`}>
          <IconChevronRight />
        </IconButton>
      )}
    </section>
  );
};

export default React.memo(Tabs);
